/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {List, ListItemText} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import SplitInformationView from './SplitInformationView';
import TabContainer from './TabContainer';
import {getMFGData, getMFGInfo} from './ViewDeviceEditorUtils';

export interface ManufacturingTabType {
  selectedTab: Number;
  tabIndex: Number;
  manufacturingInfo: GenericObject;
  isCMF?: boolean;
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  paraStyle : {
    margin : '10px 4px 20px 6px'
  },
  headerItem : {
    ...dlsAlt('typography', 'label1'),
    fontWeight : '700',
    color      : dlsColor('grey1')
  },
  bodyItem : {
    ...dlsAlt('typography', 'label2'),
    color         : dlsColor('grey1'),
    fontSize      : '14px',
    letterSpacing : 0,
    padding       : '5px'
  },
  listSecText : {
    color    : dlsColor('black'),
    fontSize : '14px'
  }
}));

/**
 * ManufacturingTab component that displays the Device manufacturing
 * data for the selected Device Details tab.
 */
export const ManufacturingTab = ({
  manufacturingInfo,
  isCMF,
  tabIndex = 0,
  selectedTab = 0
}: ManufacturingTabType) => {
  const classes = useStyles();
  const mfgInfo = getMFGInfo(manufacturingInfo);
  const {
    model,
    rawData,
    androidOS,
    mfgdataraw,
    partNumber,
    buildNumber,
    description,
    kernelVesion,
    unitpartNumber,
    currentMobileId,
    boardlevelPartNumber
  } = manufacturingInfo || {};

  if (isCMF) {
    return (
      <TabContainer key={'detailsTab'} tabIndex={tabIndex} selectedTab={selectedTab}>
        <SplitInformationView text={getMFGData(rawData, mfgInfo)}>
          <List>
            <ListItemText className={classes.headerItem}
              primary={translate('admin-devices:view.manufacturing.androidOSVersion')}
              secondary={<p className={classes.paraStyle}><span className={classes.listSecText} >{androidOS ?? ''}</span></p>} />
            <ListItemText className={classes.headerItem}
              primary={translate('admin-devices:view.manufacturing.buildNumber')}
              secondary={<p className={classes.paraStyle}><span className={classes.listSecText}>{buildNumber ?? ''}</span></p>} />
            <ListItemText className={classes.headerItem}
              primary={translate('admin-devices:view.manufacturing.mobileId')}
              secondary={<p className={classes.paraStyle}><span className={classes.listSecText}>{currentMobileId ?? ''}</span></p>} />
            <ListItemText className={classes.headerItem}
              primary={translate('admin-devices:view.manufacturing.kernelVersion')}
              secondary={<p className={classes.paraStyle}><span className={classes.listSecText}>{kernelVesion ?? ''}</span></p>} />
            <ListItemText className={classes.headerItem}
              primary={translate('admin-devices:view.manufacturing.model')}
              secondary={<p className={classes.paraStyle}><span className={classes.listSecText}>{model ?? ''}</span></p>} />
          </List>
        </SplitInformationView>
      </TabContainer>
    );
  }

  return (
    <TabContainer key={'detailsTab'} tabIndex={tabIndex} selectedTab={selectedTab}>
      <SplitInformationView text={getMFGData(mfgdataraw, mfgInfo)}>
        <List>
          <ListItemText className={classes.headerItem}
            primary={translate('admin-devices:view.manufacturing.unitPartNumber')}
            secondary={<p className={classes.paraStyle}><span className={classes.listSecText} >
              {unitpartNumber ?? partNumber}
            </span></p>} />
          <ListItemText className={classes.headerItem}
            primary={translate('admin-devices:view.manufacturing.boardPartNumber')}
            secondary={<p className={classes.paraStyle}><span className={classes.listSecText}>
              {boardlevelPartNumber ?? partNumber}
            </span></p>} />
          <ListItemText className={classes.headerItem}
            primary={translate('admin-devices:view.manufacturing.description')}
            secondary={<p className={classes.paraStyle}><span className={classes.listSecText}>{description ?? ''}</span></p>} />
        </List>
      </SplitInformationView>
    </TabContainer>
  );
};

export default ManufacturingTab;
